import React from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../../api/client';
import { useApi } from '../../api/useApi';
import { SCORE_STATUS_BADGE } from '../evaluatorData';

export default function AssignedApplicants() {
  const navigate = useNavigate();
  const { data, loading, error } = useApi(() => API.fiAssigned());
  const [search, setSearch] = React.useState('');
  const [statusFilter, setStatusFilter] = React.useState('All');
  const [sortKey, setSortKey] = React.useState<'company' | 'score' | 'status'>('company');
  const [sortDir, setSortDir] = React.useState<'asc' | 'desc'>('asc');

  const toggleSort = (key: 'company' | 'score' | 'status') => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Numerical columns default to descending (highest first) on first click.
      setSortDir(key === 'score' ? 'desc' : 'asc');
    }
  };
  const SortIcon = ({ col }: { col: 'company' | 'score' | 'status' }) => {
    if (sortKey !== col) return <span className="sort-icon">↕</span>;
    return <span className="sort-icon active">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  if (loading) return <div className="phil-loading">Loading…</div>;
  if (error || !data) return <div className="phil-error">Unable to load assigned applicants. {error}</div>;

  const { applicants, evaluator } = data;
  const submitted = applicants.filter((a: any) => a.status === 'Submitted').length;

  const query = search.trim().toLowerCase();
  const filtered = applicants.filter((a: any) => {
    const matchesSearch =
      !query ||
      (a.company && a.company.toLowerCase().includes(query)) ||
      (a.tagline && a.tagline.toLowerCase().includes(query));
    const matchesStatus = statusFilter === 'All' || a.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const STATUS_ORDER: Record<string, number> = { 'Not Started': 0, Draft: 1, Submitted: 2 };
  const sorted = [...filtered].sort((a: any, b: any) => {
    let cmp = 0;
    if (sortKey === 'company') {
      cmp = (a.company || '').localeCompare(b.company || '');
    } else if (sortKey === 'score') {
      // Unscored (null) always sorts to the bottom regardless of direction.
      const av = a.myAverage, bv = b.myAverage;
      if (av == null && bv == null) cmp = 0;
      else if (av == null) return 1;
      else if (bv == null) return -1;
      else cmp = av - bv;
    } else if (sortKey === 'status') {
      cmp = (STATUS_ORDER[a.status] ?? 99) - (STATUS_ORDER[b.status] ?? 99);
    }
    return sortDir === 'asc' ? cmp : -cmp;
  });

  return (
    <>
      <div className="phil-page-heading">Assigned Applicants</div>
      <div className="phil-page-sub">
        {evaluator.name} · {evaluator.org} · {evaluator.cycle} — {submitted} of {applicants.length} scored
      </div>

      <div className="assigned-filters">
        <input
          className="phil-input"
          type="search"
          placeholder="Search companies…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: 1, minWidth: 200 }}
        />
        <select
          className="phil-select"
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{ maxWidth: 200 }}
        >
          <option value="All">All statuses</option>
          <option value="Not Started">Not Started</option>
          <option value="Draft">Draft</option>
          <option value="Submitted">Submitted</option>
        </select>
      </div>

      <div className="phil-card">
        <table className="phil-table">
          <thead>
            <tr>
              <th className="phil-th-sort" onClick={() => toggleSort('company')}>Company<SortIcon col="company" /></th>
              <th className="phil-th-sort" onClick={() => toggleSort('score')}>My Average Score<SortIcon col="score" /></th>
              <th className="phil-th-sort" onClick={() => toggleSort('status')}>Status<SortIcon col="status" /></th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center', color: '#999', padding: '24px 0' }}>
                  No applicants match your search.
                </td>
              </tr>
            )}
            {sorted.map((a: any) => (
              <tr key={a.id} className="phil-row-link" onClick={() => navigate(`/evaluate/scoring/${a.id}`)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{a.company}</div>
                  <div className="assigned-tagline">{a.tagline}</div>
                </td>
                <td style={{ fontWeight: 700, color: a.myAverage != null ? '#00AEEF' : '#ccc' }}>
                  {a.myAverage != null ? a.myAverage.toFixed(1) : '—'}
                </td>
                <td><span className={SCORE_STATUS_BADGE[a.status as keyof typeof SCORE_STATUS_BADGE] || 'phil-badge phil-badge-draft'}>{a.status}</span></td>
                <td className="phil-arrow">{a.status === 'Submitted' ? 'View →' : 'Score →'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
